<script lang="ts">
    
    import { onMount } from 'svelte';
    import { navigate } from 'svelte-navigator';
    import { fly, fade } from 'svelte/transition';
    import type { FeedbackDto, QuestionDto } from "../../global";
    import { avatar } from '../../Services/avatar';
    import Audio from '../Audio.svelte';
    import Clock from "../Clock.svelte";
    import Countdown from "../Countdown.svelte";
    import IconBtn from '../IconBtn.svelte';
    import LottiePlayer from '../LottiePlayer.svelte';
    import Music from '../Music.svelte';
    import Points from "../Points.svelte";
    import Question from "./Question.svelte";
    import { questions, feedback_level_1, feedback_level_2 } from '../../trivial';

    type ImgPreload = {
        image: string,
        onLoad: () => void,
    }

    const gameTime = 90;

    let innerQuestions: QuestionDto[];
    let currentQuestion: QuestionDto;
    let feedbackMessage: FeedbackDto;
    let time = gameTime;
    let points = 0;
    let livesLeft = 3;
    let clock: Clock;
    let showCountdown = false;
    let showGame = false;
    let showScore = false;
    let themeFactor = 1;
    let shake = false;
    let winAudio: Audio;
    let failAudio: Audio;
    let clockMusic: Music;
    let imagesToPreload: ImgPreload[] = []
    let playAgain = false;

    onMount(startTrivial)

    function onCountdownEnd(): void {
        startTrivial();
    }

    async function startTrivial(): Promise<void> {
        innerQuestions = [...questions[$avatar.name]];
        if(!playAgain) {
            await preloadImages(
                innerQuestions
                    .filter(({ image }) => !!image)
                    .map(({ image }) => image)
            );
        }
        innerQuestions.sort(() => -0.5 + Math.random());
        nextQuestion();
        clock.start();
        showCountdown = false;
        showGame = true;
        clockMusic?.play();
    }

    async function preloadImages(images: string[]): Promise<void> {
        const promises: Promise<void>[] = [];
        imagesToPreload = images.map(image => {
            let _resolve: () => void;
            const onLoad = () => _resolve();
            promises.push(new Promise<void>(resolve => {
                _resolve = resolve;
            }))
            return {
                image,
                onLoad,
            }
        })
        await Promise.all(promises)
    }

    function resetGame(): void {
        //Cambiamos la variable time 2 veces para provocar
        //un cambio detectable por svelte
        time = 0;
        time = gameTime;
        points = 0;
        livesLeft = 3;
        showScore = false;
        innerQuestions = undefined;
        themeFactor = 1;
        shake = false;
        showGame = true;
        playAgain = true;
        showCountdown = true;
    }

    function nextQuestion(): void {
        if (innerQuestions.length === 0) {
            //No quedan preguntas
            //Terminamos el juego
            //Sumamos los segundos restantes como puntos
            points = points + clock.getTimeLeft();
            endGame();
            return;
        }
        currentQuestion = innerQuestions.shift();
    }

    async function onWin(): Promise<void> {
        winAudio?.play();
        await waitMiliseconds(600);
        points = points + themeFactor * 10;
        nextQuestion();
    }

    async function onFail(): Promise<void> {
        failAudio?.play();
        shake = true;
        await waitMiliseconds(1500);

        livesLeft = livesLeft - 1;

        if (livesLeft === 0) {
            //No quedan vidas
            //El juego ha terminado
            await endGame();
            return;
        }
        
        nextQuestion();
    }

    async function endGame(): Promise<void> {
        currentQuestion = null;
        clock.pause();
        showGame = false;
        clockMusic?.pause();
        await waitMiliseconds(600);

        //Seleccionamos el feedback apropiado
        const feedbacks = $avatar.name == 'novato' ? feedback_level_1 : feedback_level_2
        const feedbackMessages = [...feedbacks];
        feedbackMessages.sort((a, b) => a.score > b.score ? -1 : 1);
        feedbackMessage = feedbackMessages.find(({ score }) => score <= points);

        showScore = true;
    } 

    function waitMiliseconds(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    function onTrivialAnimationEnd(): void {
        shake = false;
    }

    function onHome(): void {
        navigate('inicio');
    }

    function onReset(): void {
        resetGame();
    }

</script>

<svelte:head>
    {#each imagesToPreload as { image, onLoad }}
        <link
            rel="preload"
            as="image"
            href={image}
            on:load={onLoad}
            on:error={onLoad}
            />
    {/each}
</svelte:head>

<Audio bind:this={winAudio} src="sound/win.mp3" volume={0.2} />
<Audio bind:this={failAudio} src="sound/fail.mp3" volume={0.2} />
<Music bind:this={clockMusic} src="sound/clock.mp3" volume={0.12} />

{#if showScore}
    <img src="images/fondo-trivial-genio-puntuacion.svg" alt="Ayuntamiento de Telde" class='bg-img'>
{/if}

<div class="trivial" transition:fade class:shake on:animationend={onTrivialAnimationEnd}>

    {#if !showScore}
        <div class="header">
            <Clock {time} bind:this={clock}  on:end={endGame} />
            <Points {points} />
        </div>
    {/if}

    {#if showCountdown}
            <Countdown on:end={onCountdownEnd} />
    {/if}

    {#if showGame}
        <div class="game">
            <div class="question">
                <Question
                    question={currentQuestion}
                    on:win={onWin}
                    on:fail={onFail}
                    />
            </div>
            <div class="lives">
                {#each [0, 1, 2] as number}
                    <div class="heart" transition:fly={{ y: 100, delay: number * 100 }} class:available={livesLeft >= (number + 1)}>
                        <img class="shadow" src="images/heart.svg" alt="❤️">
                    </div>
                {/each}
            </div>
        </div>
    {/if}

    {#if showScore}
        <div class="score">
            <div class="feedback">
                <div class="avatar" in:fade>
                    <LottiePlayer src={feedbackMessage.lottieSrc} />
                </div>
                <div class='points-block'>
                    <span class='score-points'>{points} pts</span>
                </div>
                {@html feedbackMessage.message}
            </div>
            <div class="buttons" in:fly={{ y: 200 }}>
                <!-- <IconBtn icon="images/home.svg" text="INICIO" on:click={onHome} /> -->
                <button class='home-btn' on:click={onHome}>
                    <img src='images/home.svg' alt='' />
                    INICIO
                </button>

                <IconBtn icon="images/reload.svg" text="OTRA PARTIDA" on:click={onReset} />
            </div>
        </div>
    {/if}

</div>


<style lang="scss">

    .home-btn {
        display: block;
        width: calc(100% - 2rem);
        padding: 0.5rem;
        margin: .5rem;
        background-color: var(--win-color);
        border-radius: 1000rem;
        color: #fff;
        font-size: calc(1.5vh + 1.5vw);
        font-family: var(--font-londrina) !important;
        letter-spacing: 0.1rem;
        border: none !important;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;

        img {
            width: 3vw !important;
            height: 3vh !important;
        }

        @media screen and (max-width: 768px) {
            font-size: calc(2vh + 2vw);

            img {
                width: 7vw !important;
                height: 7vh !important;
            }
        }

        @media screen and (max-width: 900px) {
            font-size: calc(1.7vh + 1.7vw);

            img {
                width: 5vw !important;
                height: 5vh !important;
            }
        }
    }

    .trivial {
        position: relative;
        width: 100%;
        height: 100%;
        animation-duration: 600ms;
        animation-iteration-count: 1;
        animation-fill-mode: backwards;
        display: flex;
        flex-direction: column;

        &.shake {
            animation-name: error-shake;
        }
    }

    .header {
        display: flex;
        justify-content: space-between;
        padding: 1rem;
    }

    .game{
        overflow-y: auto;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        // justify-content: space-between;
    }

    .question {
        // flex: 1 1 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 1rem;
    }

    .lives {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 1rem;
        height: 100%;
    }

    .heart {
        $size: 60px;
        width: 10vw;
        height: 10vh;
        max-width: 60px;
        filter: grayscale(1);

        img {
            width: 100%;
            height: 100%;
        }
        
        &.available {
            filter: grayscale(0);
        }
    }

    .score {
        flex: 1 1 0;
        flex-direction: column;
        min-height: 0;
        display: flex;
        text-align: center;

        img {
            $size: 4rem;
            width: 12vw;
            height: 12vh;
        }
    }

    .score-points {
        font-size: calc(2.2vh + 2.2vw);
        color: #fff;

        @media screen and (max-width: 768px) {
            font-size: calc(3vh + 3vw);
        }
    }

    .points-block {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 1.5rem;
        margin-top: 1.5rem;
        flex-direction: column;
    }

    .bg-img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        z-index: -1;
    }

    .avatar {
        flex: .5 .5 0;
        min-height: 0;
    }

    .feedback {
        color: #fff;
        text-align: center;
        padding: 1rem 3rem;
        flex-grow: 1;

        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

    }

    

    .buttons {
        padding: 1rem;
        display: flex;
        justify-content: space-between;
    }

    @keyframes error-shake {
        0% { transform: translate(1px, 1px) rotate(0deg); }
        10% { transform: translate(-1px, -2px) rotate(-1deg); }
        20% { transform: translate(-3px, 0px) rotate(1deg); }
        30% { transform: translate(3px, 2px) rotate(0deg); }
        40% { transform: translate(1px, -1px) rotate(1deg); }
        50% { transform: translate(-1px, 2px) rotate(-1deg); }
        60% { transform: translate(-3px, 1px) rotate(0deg); }
        70% { transform: translate(3px, 1px) rotate(-1deg); }
        80% { transform: translate(-1px, -1px) rotate(1deg); }
        90% { transform: translate(1px, 2px) rotate(0deg); }
        100% { transform: translate(1px, -2px) rotate(-1deg); }
    }

</style>
